/**
 * Owner-display resolution for a single prepaid coverage row.
 *
 * A coverage's ``owner`` (the billing account it was written against) and
 * ``effective_owner`` (whichever account currently carries it once
 * ``follows_radio`` ownership is resolved) are usually the same account —
 * only a ``follows_radio`` coverage whose radio has moved shows two names.
 */

/** Read a ref-shaped `{id, name}` field, falling back to a legacy flat
 * `<field>_name` string so a record produced by an older API shape still
 * resolves to a display name. */
function refName(coverage, refField, legacyField) {
  return coverage?.[refField]?.name ?? coverage?.[legacyField] ?? null;
}

/**
 * Resolve the owner name(s) a prepaid coverage row should display.
 *
 * @param {object} coverage - a prepaid coverage row (owner/effective_owner
 *   refs, or the legacy owner_name/effective_owner_name flat strings).
 * @returns {{ownerName: string|null, effectiveOwnerName: string|null, ownersDiffer: boolean}}
 */
export function resolveCoverageOwners(coverage) {
  const ownerName = refName(coverage, 'owner', 'owner_name');
  const effectiveOwnerName = refName(coverage, 'effective_owner', 'effective_owner_name');
  return {
    ownerName,
    effectiveOwnerName,
    ownersDiffer: Boolean(ownerName && effectiveOwnerName && ownerName !== effectiveOwnerName),
  };
}
